import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, UserPlus, Trash } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-30: Invite Buyer modal.
 *
 * From the destinations table, an admin can attach a buyer user (read-only
 * account) to a destination. The buyer receives an email invite; once they
 * accept, they can log in and see /buyer.
 *
 * Lists existing buyers linked to this destination + allows unlink.
 */
export default function InviteBuyerModal({ destination, onClose, linkCompany = false }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/delivery/destinations/${destination.id}/buyers`);
      setBuyers(data.buyers || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load buyers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [destination.id]);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Valid email required");
    setBusy(true);
    try {
      const { data } = await api.post(
        `/delivery/destinations/${destination.id}/invite-buyer`,
        { email, name, send_email: true, ...(linkCompany ? { link_company: true } : {}) },
      );
      if (data.status === "already_linked") {
        toast.info(`${email} already has access to this destination`);
      } else if (data.status === "linked_existing") {
        toast.success(`Linked existing buyer ${email}`);
      } else {
        toast.success(`Invited ${email} — invite email sent`);
      }
      setEmail("");
      setName("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (user) => {
    if (!confirm(`Remove ${user.email} from this destination?`)) return;
    try {
      await api.delete(
        `/delivery/destinations/${destination.id}/buyers/${user.id}`,
      );
      toast.success(`Removed ${user.email}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      data-testid="invite-buyer-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white border-4 border-black w-full max-w-2xl p-6 brut-shadow-sm max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="label-mono">Buyer access</div>
            <h2 className="font-chivo font-black text-2xl uppercase tracking-tighter leading-tight">
              Manage buyer logins
            </h2>
            <div className="text-xs text-zinc-600 mt-1">
              {destination.name}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="invite-buyer-close"
            className="border border-black p-1 hover:bg-black hover:text-white"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <form onSubmit={invite} className="mt-4 border border-black p-4 bg-zinc-50">
          <div className="label-mono mb-2">Invite a new buyer</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-mono">Email</label>
              <input
                data-testid="invite-buyer-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-black px-3 py-2 mt-1"
                placeholder="buyer@firm.com"
              />
            </div>
            <div>
              <label className="label-mono">Name (optional)</label>
              <input
                data-testid="invite-buyer-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-black px-3 py-2 mt-1"
                placeholder="Andrew Webb"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="invite-buyer-submit"
            className="mt-3 bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold disabled:opacity-50 inline-flex items-center gap-2"
          >
            <UserPlus size={12} weight="bold" />
            {busy ? "Sending…" : "Send invite"}
          </button>
        </form>

        <div className="mt-6">
          <div className="label-mono mb-2">
            Existing buyers ({buyers.length})
          </div>
          {loading ? (
            <div className="label-mono text-zinc-500">Loading…</div>
          ) : buyers.length === 0 ? (
            <div className="text-xs text-zinc-500 py-4 text-center border border-dashed border-zinc-400">
              No buyers linked to this destination yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-xs border border-black min-w-[520px]" data-testid="buyer-list">
              <thead>
                <tr className="border-b border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                  <th className="text-left py-2 px-3">Email</th>
                  <th className="text-left py-2 px-3">Name</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="text-left py-2 px-3">Destinations</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {buyers.map((b) => (
                  <tr key={b.id} className="border-b border-zinc-200" data-testid={`buyer-row-${b.id}`}>
                    <td className="py-2 px-3 font-mono">{b.email}</td>
                    <td className="py-2 px-3">{b.name}</td>
                    <td className="py-2 px-3">
                      <span className="border border-black px-1.5 py-0.5 text-[10px] uppercase tracking-widest">
                        {b.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 font-mono">
                      {(b.buyer_destination_ids || []).length}
                    </td>
                    <td className="py-2 px-3">
                      <button
                        onClick={() => remove(b)}
                        data-testid={`buyer-remove-${b.id}`}
                        className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white"
                      >
                        <Trash size={10} className="inline" /> Unlink
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="border border-black px-4 py-2 uppercase text-xs tracking-widest hover:bg-zinc-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
